import { BODY_REGIONS, } from "@repo/contract/training";
export const INTENSITY_MIN = 1;
export const INTENSITY_MAX = 10;
/**
 * The midpoint, preselected: intensity has a safe default in a way nature and
 * when do not — "5" is an answer a person corrects by stepping, while a
 * defaulted nature would store a diagnosis nobody gave.
 */
export const INTENSITY_DEFAULT = 5;
export function emptyPainDraft() {
    return {};
}
/** Tap on the map or the list: select with defaults, or deselect entirely. */
export function toggleRegion(draft, id) {
    if (draft[id] !== undefined) {
        const { [id]: _, ...rest } = draft;
        return rest;
    }
    return {
        ...draft,
        [id]: { intensity: INTENSITY_DEFAULT, nature: null, when: null },
    };
}
export function setNature(draft, id, nature) {
    const report = draft[id];
    return report === undefined
        ? draft
        : { ...draft, [id]: { ...report, nature } };
}
export function setWhen(draft, id, when) {
    const report = draft[id];
    return report === undefined ? draft : { ...draft, [id]: { ...report, when } };
}
/** Stepped from the `Numeral` stepper; clamped so the buttons cannot leave
 *  the contract's 1–10. */
export function stepIntensity(draft, id, delta) {
    const report = draft[id];
    if (report === undefined) {
        return draft;
    }
    const intensity = Math.min(INTENSITY_MAX, Math.max(INTENSITY_MIN, report.intensity + delta));
    return { ...draft, [id]: { ...report, intensity } };
}
/** The selected regions, in the map's reading order — never insertion order,
 *  so the panels under the figure sit where the figure says they are. */
export function selectedRegions(draft) {
    return BODY_REGIONS.filter((id) => draft[id] !== undefined);
}
/**
 * Confirm is enabled only when every marked region carries all three answers
 * (`injuries.ts` would refuse anything less). Zero regions is complete: the
 * person opened the map, looked, and has nothing to mark — that is an answer.
 */
export function isComplete(draft) {
    return selectedRegions(draft).every((id) => {
        const report = draft[id];
        return (report !== undefined && report.nature !== null && report.when !== null);
    });
}
/** A stored list back into a draft: the current rows become the selection,
 *  the historical rows are kept aside to ride the next PUT unchanged. */
export function fromStored(rows) {
    const draft = {};
    const historical = [];
    for (const row of rows) {
        if (row.occurrence === "current") {
            draft[row.region] = {
                intensity: row.intensity ?? INTENSITY_DEFAULT,
                nature: row.nature,
                when: row.when,
                ...(row.note === undefined ? {} : { note: row.note }),
                ...(row.since === undefined ? {} : { since: row.since }),
                ...(row.trend === undefined ? {} : { trend: row.trend }),
            };
        }
        else {
            historical.push(row);
        }
    }
    return { draft, historical };
}
/**
 * The whole list the PUT carries: every selected region as a CURRENT report
 * with the historical branch explicitly `null` (the contract's "a `null` a
 * client had to write is a `null` it meant"), plus the untouched historical
 * rows. Throws on an incomplete draft rather than sending a row the server
 * will refuse — the screen gates on {@link isComplete} first.
 */
export function toInjuryRows(draft, historical) {
    const current = selectedRegions(draft).map((id) => {
        const report = draft[id];
        if (report === undefined ||
            report.nature === null ||
            report.when === null) {
            throw new Error(`region "${id}" is missing an answer`);
        }
        return {
            howLongAgo: null,
            intensity: report.intensity,
            nature: report.nature,
            occurrence: "current",
            region: id,
            status: null,
            type: null,
            when: report.when,
            ...(report.note === undefined ? {} : { note: report.note }),
            ...(report.since === undefined ? {} : { since: report.since }),
            ...(report.trend === undefined ? {} : { trend: report.trend }),
        };
    });
    return [...current, ...historical];
}
//# sourceMappingURL=injury-draft.js.map